import React from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { Container, Stack, AppBar, Box, CssBaseline, Divider, Drawer, IconButton, List, ListItemButton, ListItemText, Toolbar, Typography, Button, TypographyProps } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../assets/icons/logo_navbar.svg';

export default function DrawerAppBar() {

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const hasDarkHero = ['/', '/about-us', '/contact-us'].includes(location.pathname);

  const HOME = t('HOME') as any;

  const navItems = [
    {
      name: HOME.HOME,
      path: "/"
    },
    {
      name: HOME.ABOUT_US,
      path: "/about-us"
    },
    {
      name: HOME.CONTACT_US,
      path: "/contact-us"
    }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    if (mobileOpen) {
      handleDrawerToggle();
    }
  };

  const handleNavigateHome = () => {
    navigate('/');
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const BrandMark: React.FC<Pick<TypographyProps, 'sx' | 'display'>> = ({ sx }) => (
    <Typography
      variant="h6"
      component="div"
      onClick={handleNavigateHome}
      sx={{
        fontSize: 24,
        fontWeight: 500,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        columnGap: 1.5,
        color: '#F7F4F2',
        ...sx
      }}
    >
      <span>PannonianTech</span>
      <img loading="lazy" src={logo} alt='logo' />
    </Typography>
  );

  const drawer = (
    <Box sx={{ textAlign: 'center' }}>
      <BrandMark sx={{ my: 2, justifyContent: 'center' }} />
      <Divider />
      <List>
        {navItems.map((item: any) => (
          <React.Fragment key={item.name}>
            <ListItemButton onClick={() => handleItemClick(item.path)} sx={{ color: location.pathname === item.path ? "#BA1B1D !important" : "unset" }}>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        component="nav"
        position="fixed"
        sx={{
          backgroundColor: hasDarkHero
            ? 'transparent !important'
            : '#FFFFFF !important',
          boxShadow: 'none !important',
          padding: { md: '20px 0px' },
          zIndex: 1100,
        }}
      >
        <Container sx={{ maxWidth: "1440px !important", padding: { xs: "0px 16px 0px 16px !important", sm: '0px !important', md: '0px !important' } }}>
          <Toolbar
            sx={{
              backgroundColor: 'transparent !important',
              height: 74,
              padding: { md: "0px 120px 0px 100px !important" },
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                justifyContent: { xs: 'center', sm: 'flex-start' }
              }}
            >
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  p: 0,
                  display: { xs: 'flex', sm: 'none' },
                  color: hasDarkHero ? '#F7F4F2' : '#333333',
                  position: 'absolute',
                  left: 0,
                }}
              >
                <MenuIcon />
              </IconButton>

              <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: '100%', justifyContent: 'center' }}>
                <BrandMark sx={{ pointerEvents: 'auto' }} />
              </Box>

              <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexGrow: 1 }}>
                <BrandMark />
              </Box>

              <Stack direction="row" spacing={3} sx={{ display: { xs: 'none', sm: 'flex' }, ml: 'auto' }}>
                {navItems.map((item: any) => (
                  <Button
                    key={item.name}
                    id={item.name}
                    aria-haspopup="true"
                    onClick={() => handleItemClick(item.path)}
                  >
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: (item.path === location.pathname) ? 700 : 400,
                        lineHeight: "24px",
                        color: (item.path === location.pathname) ? "#BA1B1D !important" : "#E9ECEE !important"
                      }}>
                      {item.name}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}